import { useQuery } from '@tanstack/react-query';

import { api } from './api';

type LibraryFilters = Parameters<typeof api.getLibrary>[1];

export function useApps() {
  return useQuery({
    queryKey: ['apps'],
    queryFn: api.apps,
    staleTime: 30_000,
  });
}

export function useOverallStreak() {
  return useQuery({
    queryKey: ['streak', 'overall'],
    queryFn: api.overallStreak,
    staleTime: 30_000,
  });
}

export function useOverallStats() {
  return useQuery({
    queryKey: ['stats', 'overall'],
    queryFn: api.overallStats,
    staleTime: 30_000,
  });
}

export function useAppStats(appId: string | undefined) {
  return useQuery({
    queryKey: ['apps', appId, 'stats'],
    queryFn: () => api.appStats(appId!),
    enabled: !!appId,
    staleTime: 30_000,
  });
}

/** Cards currently flagged as "keeps getting me", worst first. */
export function useAppStruggling(appId: string | undefined) {
  return useQuery({
    queryKey: ['apps', appId, 'struggling'],
    queryFn: () => api.strugglingCards(appId!),
    enabled: !!appId,
    staleTime: 30_000,
  });
}

export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: api.getSettings,
    staleTime: 30_000,
  });
}

export function useAppConfig<T extends Record<string, unknown>>(appId: string | undefined) {
  return useQuery({
    queryKey: ['apps', appId, 'config'],
    queryFn: () => api.getAppConfig<T>(appId!),
    enabled: !!appId,
    staleTime: 30_000,
  });
}

export function useLibrary(appId: string | undefined, filters: LibraryFilters = {}) {
  return useQuery({
    queryKey: ['apps', appId, 'library', filters],
    queryFn: () => api.getLibrary(appId!, filters),
    enabled: !!appId,
    staleTime: 10_000,
  });
}

export function useSimulationHistory(enabled: boolean = true) {
  return useQuery({
    queryKey: ['einbuergerungstest', 'simulation', 'history'],
    queryFn: api.einbuergerungstest.getHistory,
    enabled,
    staleTime: 30_000,
  });
}

export function useInProgressSimulation(enabled: boolean = true) {
  return useQuery({
    queryKey: ['einbuergerungstest', 'simulation', 'in-progress'],
    queryFn: api.einbuergerungstest.getInProgress,
    enabled,
    staleTime: 0, // always re-check on mount
  });
}

/**
 * The languages the user wants things explained in.
 *
 * Reads the setting that has existed since the first release and was honoured
 * nowhere: every card face was handed the literals `"en"` and `"pt"`. That was
 * invisible while one Brazilian used the app and exactly wrong the moment a
 * German joined, who needs German and English.
 *
 * Falls back to the same pair the code used to hardcode, so a first render
 * before settings load looks like it always did rather than blank.
 */
export function useAuxLanguages(): { primaryLang: string; secondaryLang: string } {
  const settings = useSettings();
  return {
    primaryLang: settings.data?.user.primary_aux_lang ?? 'en',
    secondaryLang: settings.data?.user.secondary_aux_lang ?? 'pt',
  };
}
